import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { validateInviteToken, saveInviteDetails } from '../api/staff';


interface OnboardPageProps {
  token: string;
}

interface InviteInfo {
  email:                string;
  firstName:            string;
  lastName:             string;
  role:                 string;
  phone?:               string;
  jobTitle?:            string;
  hasCompletedDetails?: boolean;
}

export function OnboardPage({ token }: OnboardPageProps) {
  const { loginWithRedirect } = useAuth0();
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [invite, setInvite]     = useState<InviteInfo | null>(null);
  const [phone, setPhone]       = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [saving, setSaving]     = useState(false);
  const [step, setStep]         = useState<'details' | 'signup'>('details');

  useEffect(() => {
    validateInviteToken(token)
      .then((info: InviteInfo) => {
        setInvite(info);
        // Pre-fill whatever details were already captured on a prior visit
        if (info.phone)    setPhone(info.phone);
        if (info.jobTitle) setJobTitle(info.jobTitle);
        // Skip the details step entirely if the user already completed it
        // — was asking for mobile on every revisit before this fix.
        if (info.hasCompletedDetails) setStep('signup');
      })
      .catch(() => setError('This invitation link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmitDetails = async () => {
    setSaving(true);
    setError('');
    try {
      await saveInviteDetails(token, { phone: phone.trim() || undefined, jobTitle: jobTitle.trim() || undefined });
      setStep('signup');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSignup = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: invite?.email,
      },
      appState: { invite_token: token },
    });
  };

  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        login_hint: invite?.email,
      },
      appState: { invite_token: token },
    });
  };

  if (loading) {
    return (
      <div className="shell">
        <div className="shell-spinner" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="shell">
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ color: 'var(--fg)', fontSize: 20, marginBottom: 8 }}>Invalid Invitation</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>{error}</p>
          <p style={{ color: 'var(--subtle)', fontSize: 12, marginTop: 16 }}>
            Contact your administrator to get a new invitation link.
          </p>
        </div>
      </div>
    );
  }

  if (!invite) return null;

  // Step 2: Details saved — now prompt Auth0 signup
  if (step === 'signup') {
    return (
      <div className="shell">
        <div style={{ textAlign: 'center', maxWidth: 460, padding: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ color: 'var(--fg)', fontSize: 22, marginBottom: 8 }}>
            You're set up, {invite.firstName}
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
            Your details are saved. Complete the last step — connect a login.
          </p>

          {/* Log In is primary: if the user already has an Auth0 account
              (previous signup attempt, same email used elsewhere), Sign Up
              will fail with a confusing error. Log In works in that case. */}
          <button
            className="submit-btn"
            onClick={handleLogin}
            style={{ width: '100%', fontSize: 15, padding: '14px 0', marginBottom: 10 }}
          >
            Log In (I already have an account)
          </button>
          <button
            onClick={handleSignup}
            style={{
              width: '100%', fontSize: 13, padding: '12px 0', borderRadius: 8, cursor: 'pointer',
              background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)',
              marginBottom: 14,
            }}
          >
            Create a new account
          </button>

          <div style={{
            padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, fontSize: 11, color: 'var(--muted)', lineHeight: 1.6, textAlign: 'left',
          }}>
            <strong style={{ color: 'var(--fg)' }}>Tip:</strong> if "Create a new account" shows
            "something went wrong", try "Log In" instead — your email ({invite.email}) may already
            exist in the login system from a previous attempt.
          </div>
          <p style={{ color: 'var(--subtle)', fontSize: 11, marginTop: 14 }}>
            Having trouble? Your admin can resend the invite or contact support.
          </p>
        </div>
      </div>
    );
  }

  // Step 1: Collect details
  return (
    <div className="shell" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16,
        padding: 32, maxWidth: 480, width: '100%',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <svg width={36} height={36} viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12 }}>
            <path d="M20 2L36 11V29L20 38L4 29V11L20 2Z" fill="rgba(45,212,191,0.12)" stroke="#2dd4bf" strokeWidth="1.5" />
            <path d="M20 8L30 14V26L20 32L10 26V14L20 8Z" fill="rgba(45,212,191,0.2)" />
            <circle cx="20" cy="18" r="4" fill="#2dd4bf" />
            <circle cx="20" cy="18" r="2" fill="#0d0e14" />
          </svg>
          <h2 style={{ color: 'var(--fg)', fontSize: 20, marginBottom: 4 }}>Welcome to CIMA</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.5 }}>
            You've been invited to join the Critical Incident Management team.
            Please fill in your details below.
          </p>
        </div>

        {/* Invite info badge */}
        <div style={{
          background: 'var(--surface)', borderRadius: 8, padding: '12px 16px',
          marginBottom: 20, border: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Email</span>
            <span style={{ fontSize: 12, color: 'var(--fg)', fontWeight: 600 }}>{invite.email}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Name</span>
            <span style={{ fontSize: 12, color: 'var(--fg)', fontWeight: 600 }}>{invite.firstName} {invite.lastName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Role</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
              background: 'var(--primary-mid)', color: 'var(--primary)',
            }}>
              {invite.role}
            </span>
          </div>
        </div>

        {/* Details form */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>
            Mobile Phone *
          </label>
          <input
            className="form-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+61 4xx xxx xxx"
            style={{ width: '100%', padding: '10px 12px', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--fg)', borderRadius: 8 }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>
            Job Title
          </label>
          <input
            className="form-input"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Safety Manager, Site Supervisor"
            style={{ width: '100%', padding: '10px 12px', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--fg)', borderRadius: 8 }}
          />
        </div>

        <p style={{ fontSize: 11, color: 'var(--subtle)', marginBottom: 16, lineHeight: 1.5 }}>
          Your on-call availability will be set by your admin after onboarding.
        </p>

        {error && (
          <p style={{
            fontSize: 12, color: 'var(--accent-red)',
            background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
            borderRadius: 6, padding: '8px 10px', marginBottom: 12,
          }}>⚠ {error}</p>
        )}

        <button
          className="submit-btn"
          onClick={handleSubmitDetails}
          disabled={saving || !phone.trim()}
          style={{ width: '100%', fontSize: 14, padding: '12px 0' }}
        >
          {saving ? 'Saving...' : 'Continue to Account Setup'}
        </button>

        <p style={{ color: 'var(--subtle)', fontSize: 11, textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
          After filling in your details, you'll create your login credentials via Auth0.
        </p>
      </div>
    </div>
  );
}
